import { FC, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getRealWorldProject } from '../data/realWorldProjects';

const RealWorldProjectDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const project = id ? getRealWorldProject(id) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <Page>
        <Inner>
          <h1>Project not found</h1>
          <BackLink to="/#real-world-projects">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to projects
          </BackLink>
        </Inner>
      </Page>
    );
  }

  return (
    <Page>
      <Inner>
        <BackLink to="/#real-world-projects">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to real world projects
        </BackLink>

        <Header>
          <HeroImage src={project.cardImage} alt={project.title} />
          <div>
            <Title>{project.title}</Title>
            {project.tags && project.tags.length > 0 && (
              <TagRow>
                {project.tags.map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </TagRow>
            )}
          </div>
        </Header>

        <Prose>
          <p>{project.detailIntro}</p>
          {project.detailParagraphs.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </Prose>

        {project.galleryImages.length > 0 && (
          <>
            <GalleryTitle>Gallery</GalleryTitle>
            <Gallery>
              {project.galleryImages.map((img, i) => (
                <Figure key={i}>
                  <img src={img.src} alt={img.caption ?? `${project.title} ${i + 1}`} />
                  {img.caption && <figcaption>{img.caption}</figcaption>}
                </Figure>
              ))}
            </Gallery>
          </>
        )}
      </Inner>
    </Page>
  );
};

const Page = styled.main`
  flex: 1;
  background: #f8f9fa;
  padding: 2rem 0 4rem;
`;

const Inner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 0 1.25rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #3498db;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 2rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.header`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: 220px 1fr;
    align-items: start;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  max-height: 280px;
  object-fit: contain;
  object-position: center;
  background: #eef1f4;
  border-radius: 8px;
`;

const Title = styled.h1`
  font-family: var(--font-primary);
  font-size: 2rem;
  color: #2c3e50;
  margin: 0 0 0.75rem;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  font-size: 0.8rem;
  padding: 0.25rem 0.65rem;
  border-radius: 9999px;
  background: #e8eef2;
  color: #2c3e50;
`;

const Prose = styled.div`
  font-family: var(--font-primary);
  color: #444;
  line-height: 1.65;
  margin-bottom: 2.5rem;

  p {
    margin: 0 0 1rem;
  }
`;

const GalleryTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 1.35rem;
  color: #2c3e50;
  margin: 0 0 1rem;
`;

const Gallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Figure = styled.figure`
  margin: 0;

  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    vertical-align: middle;
  }

  figcaption {
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: #666;
  }
`;

export default RealWorldProjectDetail;
